import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import Image from "next/legacy/image";
import { Box, Button, Text } from "@chakra-ui/react";
import {
  type Page as SanityPage,
  getPage,
  type Banner,
  getAnnouncementBanner,
  getStackedLogos,
  type Customer,
} from "lib/sanity";
import { HalfHero } from "components/half-hero";
import { Section, sectionSpacingY } from "components/section";
import { StackedLogos } from "components/logos";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { Link } from "ui/link";
import { useDemoLinkWithParameters } from "hooks/use-demo-link";
import { getPageMetaImage } from "utils";
import { PairImage } from "components/pair-image";
import heroImage from "../../../public/assets/solutions/amazon-redshift/hero.png";
import fullWidthImage from "../../../public/assets/solutions/amazon-redshift/full-width.png";
import firstPairImage from "../../../public/assets/solutions/amazon-redshift/pair-1.png";
import secondPairImage from "../../../public/assets/solutions/amazon-redshift/pair-2.png";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  customerRows: Customer[][];
};

const AmazonRedshift: NextPage<Readonly<PageProps>> = ({ page, announcementBanner, customerRows }) => {
  const demoLinkWithParameters = useDemoLinkWithParameters();
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={3 / 2}
      />

      <StackedLogos customerRows={customerRows} />

      <Section center textAlign="center">
        <Text as="h2" textStyle="h4" maxW="3xl" mx="auto">
          You invested in a source of truth. Now put it to work with Hightouch.
        </Text>

        <Box pos="relative" w="100%" minH={{ base: 250, md: 500 }} my={16} maxW="4xl" mx="auto" pointerEvents="none">
          <Image src={fullWidthImage} alt="Customer data moving through Hightouch." objectFit="contain" layout="fill" />
        </Box>

        <Button as={Link} href={demoLinkWithParameters} size="lg">
          Book a demo
        </Button>
      </Section>

      <Section>
        <PairImage
          align="left"
          image={firstPairImage}
          title="Turn your warehouse into a CDP"
          description={
            <Text>
              Skip the implementation phase and duplicative data storage required with a traditional CDP. Use Hightouch
              to activate customer data and audiences directly from Redshift to supercharge advertising, sales, and
              marketing teams.
            </Text>
          }
        />
      </Section>

      <Section my={0}>
        <PairImage
          align="right"
          image={secondPairImage}
          title="Get started in minutes, not months"
          description={
            <Text>
              Using Hightouch to activate your Redshift data is secure, easy, and nearly instant. Start syncing data
              from Redshift to your business tools today.
            </Text>
          }
        />
      </Section>

      <Section my={0} mt={{ base: "biggest", md: "huge" }} pb={sectionSpacingY}>
        <CenteredQuote
          maxW="4xl"
          quoteText="The value is being able to pull in Salesforce, Marketo, Asana, NetSuite, and Lever, and blend the data from historically separate departments together for analysis. With Hightouch, we can then push it out to make sure that everyone's looking at the same metrics."
          author={{ name: "William Tsu", job: "Customer Success Operations" }}
          company={{
            name: "Blend",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/eb8e724a9b5e0f1c6ba686fb37685d051030386e-176x73.svg",
          }}
          cta={{ href: "/customers/blend", text: "Read customer story" }}
        />
      </Section>
    </Page>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, customerRows] = await Promise.all([
    getPage("/solutions/amazon-redshift"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      customerRows,
    },
    revalidate: 60,
  };
};

export default AmazonRedshift;
